/**
 * Keywords tab on the SMS page — auto-reply / list join-leave rules for
 * inbound texts. "Text JOIN to subscribe" style flows live here.
 *
 * Matching: the FIRST WORD of an inbound text (case-insensitive) against
 * this account's keywords, on any of the account's numbers. STOP always
 * wins — it's handled by the platform DNC layer before keywords run.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  smsCampaigns, type SmsKeywordRow, type KeywordAction,
} from '@/api/smsCampaigns';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { SmsListPicker } from '@/components/SmsListPicker';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Hash, Plus, Trash2, UserPlus, UserMinus, Reply } from 'lucide-react';
import { cn } from '@/lib/cn';

const ACTION_LABEL: Record<KeywordAction, string> = {
  none: 'Reply only',
  join_list: 'Join a list',
  leave_list: 'Leave a list',
};

export function KeywordsTab({ canMutate }: { canMutate: boolean }) {
  const customerId = useEffectiveCustomerId();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-keywords', customerId],
    queryFn: () => smsCampaigns.listKeywords({ customer_id: customerId ?? undefined }),
  });
  const rows = data?.rows ?? [];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground max-w-md">
            When someone texts a keyword to any of your numbers, Ivrello can
            reply automatically and add or remove them from a list.
            <span className="font-medium"> STOP/UNSUBSCRIBE always opt the sender out</span> —
            keywords can&rsquo;t override that.
          </p>
          {canMutate && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 me-1.5" />
              New keyword
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading keywords…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <Hash className="mx-auto h-7 w-7 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">No keywords yet</p>
            <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
              Example: &ldquo;Text <span className="font-mono font-semibold">JOIN</span> to
              555-0100 to get class updates.&rdquo;
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border -mx-2">
            {rows.map((k) => (
              <KeywordRow key={k.id} row={k} canMutate={canMutate} />
            ))}
          </ul>
        )}
      </CardContent>
      {canMutate && <CreateKeywordDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </Card>
  );
}

function KeywordRow({ row, canMutate }: { row: SmsKeywordRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => smsCampaigns.removeKeyword(row.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-keywords'] }),
  });
  const toggle = useMutation({
    mutationFn: () => smsCampaigns.updateKeyword(row.id, { active: !row.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-keywords'] }),
  });

  const ActionIcon = row.action_kind === 'join_list'
    ? UserPlus
    : row.action_kind === 'leave_list' ? UserMinus : Reply;

  return (
    <li className="flex items-center gap-3 px-2 py-3">
      <span className={cn(
        'inline-flex items-center rounded-md px-2.5 py-1 font-mono text-sm font-semibold tracking-wide ring-1',
        row.active
          ? 'bg-primary/5 text-primary ring-primary/20'
          : 'bg-secondary text-muted-foreground ring-border line-through',
      )}>
        {row.keyword}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ActionIcon className="h-3.5 w-3.5" />
          <span>{ACTION_LABEL[row.action_kind as KeywordAction] ?? row.action_kind}</span>
          {row.list_name && (
            <span className="truncate">
              &middot; <span className="font-medium">{row.list_name}</span>
            </span>
          )}
        </div>
        {row.reply_body && (
          <p className="mt-0.5 text-xs text-muted-foreground truncate">
            Replies: &ldquo;{row.reply_body}&rdquo;
          </p>
        )}
      </div>
      {canMutate && (
        <>
          <Button
            size="sm" variant="ghost"
            onClick={() => toggle.mutate()}
            disabled={toggle.isPending}
          >
            {row.active ? 'Disable' : 'Enable'}
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            aria-label={`Delete keyword ${row.keyword}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-slate-400" />
          </Button>
        </>
      )}
    </li>
  );
}

function CreateKeywordDialog({
  open, onOpenChange,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [keyword, setKeyword] = useState('');
  const [action, setAction] = useState<KeywordAction>('join_list');
  const [listId, setListId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const needsList = action === 'join_list' || action === 'leave_list';

  const create = useMutation({
    mutationFn: () => smsCampaigns.createKeyword({
      keyword: keyword.trim().toUpperCase(),
      action,
      list_id: needsList ? listId : null,
      reply_body: replyBody.trim() || null,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-keywords'] });
      setKeyword(''); setAction('join_list'); setListId(null); setReplyBody('');
      setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const keywordValid = /^[A-Za-z0-9]{2,20}$/.test(keyword.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New keyword</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="kw-word">Keyword</Label>
            <Input
              id="kw-word" className="font-mono uppercase"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="JOIN"
              maxLength={20}
            />
            <p className="text-xs text-muted-foreground">
              2&ndash;20 letters or digits. Matched against the first word of an
              inbound text, any capitalization. STOP, HELP, and the other
              opt-out words are reserved.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="kw-action">Action</Label>
            <Select value={action} onValueChange={(v) => setAction(v as KeywordAction)}>
              <SelectTrigger id="kw-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="join_list">Join a list (subscribe the sender)</SelectItem>
                <SelectItem value="leave_list">Leave a list (unsubscribe the sender)</SelectItem>
                <SelectItem value="none">Reply only (no list change)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {needsList && (
            <SmsListPicker
              value={listId}
              onChange={setListId}
              label={action === 'join_list' ? 'List to join' : 'List to leave'}
              allowNone={false}
            />
          )}
          <div className="space-y-1">
            <Label htmlFor="kw-reply">
              Auto-reply {action === 'none' ? '' : '(optional)'}
            </Label>
            <Textarea
              id="kw-reply" rows={2}
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              maxLength={1600}
              placeholder={action === 'join_list'
                ? 'e.g. You’re subscribed! Reply STOP to opt out.'
                : 'Message texted back to the sender'}
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={
              create.isPending
              || !keywordValid
              || (needsList && !listId)
              || (action === 'none' && !replyBody.trim())
            }
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create keyword'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
